import { type ReactElement } from 'react'
import { DashboardWidgetCard } from 'app/routes/dashboard_.$dashboardId/widgets'
import { isDefined, type DashboardDataWithEditable, type ReportDateRange } from '@terros/common'
import { Group, Stack, Text } from '@mantine/core'
import { stringifyReportDateRange, useTranslation, type DashboardFeedGroup } from '@front/model'
import { DateRangeMenu } from '@components'
import { DashboardFeedGrid } from './DashboardFeedGrid'

type Props = {
  group: DashboardFeedGroup<DashboardDataWithEditable>
  /** The section's resolved baseline range — what its inheriting widgets query. */
  dateRange: ReportDateRange
  /** The page-level pick sections fall back to before their dashboard's range. */
  homeDateRange: ReportDateRange | undefined
  /** The range explicitly picked on this section, if any. */
  selectedDateRange: ReportDateRange | undefined
  onSelectDateRange: (range: ReportDateRange) => void
  onClearDateRange: () => void
}

/**
 * One dashboard as a labeled feed section: the dashboard's name, the picker
 * that sets the range its inheriting widgets query, and the dashboard's
 * widgets in their configured order.
 *
 * The picker shows a concrete range only once one is explicitly picked —
 * until then it presents the range the widgets actually inherit (the
 * dashboard's configured range, or a neutral "Date range" when it has none),
 * since only an explicit pick takes priority over the widget and dashboard
 * ranges. A pick can be cleared back to that inheritance from the menu.
 */
export function DashboardFeedSection({
  group,
  dateRange,
  homeDateRange,
  selectedDateRange,
  onSelectDateRange,
  onClearDateRange,
}: Props): ReactElement {
  const { t } = useTranslation()
  const inheritedDateRange = homeDateRange ?? group.dashboard.dateRange
  const resetsToDashboard = isDefined(group.dashboard.dateRange) && !isDefined(homeDateRange)

  return (
    <Stack gap='sm'>
      <Group justify='space-between' wrap='nowrap'>
        <Text c='dimmed' className='font-semibold tracking-wide' lineClamp={1} size='xs' tt='uppercase'>
          {group.dashboard.name?.trim() || t('Dashboard')}
        </Text>
        <DateRangeMenu
          onChange={onSelectDateRange}
          onReset={isDefined(selectedDateRange) ? onClearDateRange : undefined}
          placeholder={stringifyReportDateRange(inheritedDateRange) ?? t('Date range')}
          resetLabel={resetsToDashboard ? t('Reset to dashboard range') : t('Clear date range')}
          value={selectedDateRange}
        />
      </Group>
      <DashboardFeedGrid widgets={group.widgets}>
        {(widget) => (
          <DashboardWidgetCard
            className='h-full'
            dashboardFilter={group.dashboard.filter}
            dateRange={dateRange}
            widget={widget}
          />
        )}
      </DashboardFeedGrid>
    </Stack>
  )
}
